import { Component, OnInit, AfterViewInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TooltipPosition } from '@angular/material/tooltip';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { startWith } from 'rxjs/internal/operators/startWith';
import { catchError } from 'rxjs/internal/operators/catchError';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { map } from 'rxjs/internal/operators/map';
import { merge } from 'rxjs/internal/observable/merge';
import { of } from 'rxjs/internal/observable/of';

import { MaterialAction, MaterialCategory, MediaFile } from '@app/models';
import { AuthService, MaterialActionService, MaterialCategoryService, MaterialHelperService, MaterialSearchTermsService, MaterialService, SharedUtilsService } from '@app/services';
import { AddMaterialDialogComponent } from '@app/components/shared/dialogs/add-material-dialog/add-material-dialog.component';
import { MaterialSortKey } from './materials-search-header/materials-search-header.component';
import { FiltersPanelResult } from './filters-panel/filters-panel.component';

@Component({
  selector: 'view-materials',
  templateUrl: './view-materials.component.html',
  styleUrl: './view-materials.component.scss',
  standalone: false
})
export class ViewMaterialsComponent implements OnInit, AfterViewInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  displayedColumns: string[] = ['title', 'categories', 'sub-categories', 'view'];
  dataSource: MatTableDataSource<MediaFile>;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @Input() item: Object;

  gridOptionsKey: string = 'view-material-options';
  isLoadingResults: boolean = true;
  loadingSearchResults: boolean = false;
  isAdmin = false;
  userIsLoggedIn = false;
  currentUser: any;

  allMedia: any[] = null;
  allMaterials: any[] = null;
  materialCategories: MaterialCategory[];
  newMaterialAction: MaterialAction;
  actionStore: any = [];

  materialPassedFromDialog: any;
  newMaterial: MediaFile;
  fileStore: any = [];

  screenSize: any;
  selectedItem: any = {};
  searchParams: MediaFile = new MediaFile();

  currentViewMode = '4 Columns';
  currentSort: MaterialSortKey = 'default';
  showFiltersPanel = false;
  selectedCategoriesMulti: string[] = [];

  itemsPerPage = 16;
  currentPage = 1;
  totalItemsCount = 0;
  defaultPageSize = 25;
  // Stable reference for the paginate pipe. Mutated in place so the pipe
  // doesn't see a new config object on every change-detection cycle.
  paginationConfig = { itemsPerPage: this.itemsPerPage, currentPage: this.currentPage, totalItems: this.totalItemsCount };
  // Template-bound cached state. Recomputed by recalcDerivedState() from
  // every handler that mutates the underlying filter inputs.
  filtersBadgeCount = 0;
  hasActiveFilters = false;
  display = 'none';
  foundAuthorToShow: any;
  isSignedIn: boolean = false;
  showMobileFilters: boolean = false;
  showAddButton: boolean = false;
  showSubCategorySelect: boolean = false;
  subCategoryToShow: any;

  positionOptions: TooltipPosition[] = ['after', 'before', 'above', 'below', 'left', 'right'];
  position = new FormControl(this.positionOptions[0]);
  position2 = new FormControl(this.positionOptions[1]);
  position3 = new FormControl(this.positionOptions[2]);

  // Set once in ngOnInit from ?page=N in the URL. Honored after the
  // localStorage restore so deep-linked / crawled URLs win over saved state.
  private urlPageOverride: number | null = null;

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _authService: AuthService,
    private _materialService: MaterialService,
    private _materialActionService: MaterialActionService,
    private _materialCategoryService: MaterialCategoryService,
    public _materialSearchTermsService: MaterialSearchTermsService,
    private _sharedService: SharedUtilsService,
    private _materialHelperService: MaterialHelperService,
    public addMaterialDialog: MatDialog,
    public flagMaterialDialog: MatDialog
  ) { }


  ngOnInit() {
    const pageParam = this._route.snapshot.queryParamMap.get('page');
    const parsed = pageParam ? parseInt(pageParam, 10) : NaN;
    if (Number.isFinite(parsed) && parsed > 0) this.urlPageOverride = parsed;

    this.initMaterialData();
  }


  ngOnDestroy() {
    if (this.currentViewMode === '4 Columns') this._materialSearchTermsService.saveTableOptionsInLocal(this.gridOptionsKey, this.currentPage, this.itemsPerPage, this.currentViewMode);
    else if (this.paginator) this._materialSearchTermsService.saveTableOptionsInLocal(this.gridOptionsKey, this.paginator.pageIndex, this.paginator.pageSize, this.currentViewMode);

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }


  ngAfterViewInit() {
    window.onresize = (() => {
      if (!document.activeElement.classList.contains('preventOnresize')) this.setMobileMode();
    });
  }


  async initMaterialData() {
    this.currentUser = this._authService.getCurrentlyLoggedInUser();
    // console.log('Current user: ', this.currentUser);

    if (this.currentUser?._id) {
      this.isSignedIn = true;
      this.isAdmin = this._authService.checkForAdminPrivileges(this.currentUser);
    } else this.isSignedIn = false;

    await this.getMaterialCategories();
    this.setMobileMode();
    this.initializeTable();
  }


  setMobileMode() {
    const screenWidth = window?.innerWidth || document?.documentElement?.clientWidth || document?.body?.clientWidth;
    if (screenWidth <= 768) {
      if (this.currentViewMode === 'List View') {
        this.currentViewMode = '4 Columns';
        this.setViewType('4 Columns');
      }
      this.showAddButton = false;
    } else if (screenWidth > 768) {
      this.showMobileFilters = true;
      this.showAddButton = true;
    }
  }


  toggleMobile() {
    this.showMobileFilters = !this.showMobileFilters;
  }


  onInputClick(e) {
    e.preventDefault()
    e.stopPropagation()
  }


  getMaterialCategories() {
    return new Promise(async (resolve) => {
      const foundCategories = await lastValueFrom(this._materialCategoryService.getMaterialCategories());
      this.materialCategories = foundCategories?.length ? foundCategories : [];

      resolve(null);
    });
  }


  setViewType(viewType) {
    if (viewType === this.currentViewMode) return; // Don't want to reload data if this already the view type.

    this.currentViewMode = viewType;
    this.currentPage = 1;
    if (this.paginator) this.paginator.pageIndex = 0;

    this.isLoadingResults = true; // Flip flag to show that results are loading.
    this.loadingSearchResults = true;
    this.getPageData();
  }


  checkForSubs(event) {
    this.subCategoryToShow = null;

    this.materialCategories.forEach(element1 => {
      if (element1.name === event && element1.subCategories.length) this.subCategoryToShow = element1;
    });

    if (this.subCategoryToShow) this.showSubCategorySelect = true;
    else {
      this.showSubCategorySelect = false;
      this.subCategoryToShow = null;
      this.searchParams.subCategories = '';
    }
  }


  changeThePage(pageNo) {
    this.currentPage = pageNo;
    this.syncPaginationConfig();
    this._materialSearchTermsService.saveTableOptionsInLocal(this.gridOptionsKey, this.currentPage, this.itemsPerPage, this.currentViewMode);
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { page: pageNo > 1 ? pageNo : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.getPageData();
  }


  // Crawlable href for the pagination anchors so search bots can follow
  // pagination instead of seeing onclick-only links. Preserves any other
  // query params already on the URL via the Router's UrlTree.
  getPageHref(pageValue: number): string {
    const tree = this._router.createUrlTree([], {
      relativeTo: this._route,
      queryParams: { page: pageValue > 1 ? pageValue : null },
      queryParamsHandling: 'merge',
    });
    return this._router.serializeUrl(tree);
  }


  onPageLinkClick(event: MouseEvent, pageValue: number, api: any) {
    event.preventDefault();
    api.setCurrent(pageValue);
  }


  trackByPageValue(_index: number, page: any) {
    return page.value;
  }


  resetPageNumbers() {
    this.currentPage = 1;
    this.syncPaginationConfig();
    if (this.paginator) this.paginator.pageIndex = 0;
  }


  initializeTable() {
    const termsFromStorage = this._materialSearchTermsService.getSearchTermsFromLocal(this.gridOptionsKey);
    if (termsFromStorage) {
      this.searchParams = termsFromStorage;

      if (this.searchParams.categories && this.searchParams.categories !== '') this.checkForSubs(this.searchParams.categories);

      const additional = this._materialSearchTermsService.getAdditionalOptionsFromLocal(this.gridOptionsKey);
      if (additional?.sort && ['default', 'newest', 'alphabetical', 'popular'].includes(additional.sort)) {
        this.currentSort = additional.sort as MaterialSortKey;
      }
      if (Array.isArray(additional?.selectedCategoriesMulti)) {
        this.selectedCategoriesMulti = additional.selectedCategoriesMulti;
      }

      const savedTableOptions = this._materialSearchTermsService.getTableOptionsFromLocal(this.gridOptionsKey);
      if (savedTableOptions) {
        this.currentViewMode = savedTableOptions.pageMode;
        if (this.currentViewMode === '4 Columns') this.currentPage = savedTableOptions.pageNumber;
        else {
          this.defaultPageSize = savedTableOptions.pageSize;
          if (this.paginator) {
            this.paginator.pageSize = savedTableOptions.pageSize;
            this.paginator.pageIndex = savedTableOptions.pageNumber;
          }
        }
      }

      if (this.sort) {
        this.sort.sortChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
          if (this.paginator) this.paginator.pageIndex = 0;
          this.getPageData();
        });
      }

      if (this.paginator) {
        this.paginator.page.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => this.getPageData());
      }
    }

    if (this.urlPageOverride && this.currentViewMode === '4 Columns') {
      this.currentPage = this.urlPageOverride;
    }

    this.recalcDerivedState();

    if (this.searchParams && this.searchParams['hasSearchTerms']) this.applySearchResults();
    else this.getPageData();
  }


  applySearchResults() {
    this.searchParams['hasSearchTerms'] = this.hasCurrentAdvancedSearchParams();
    if (!this.searchParams['hasSearchTerms']) this.resetPageNumbers();
    this.recalcDerivedState();
    this._materialSearchTermsService.saveSearchTermsInLocal(this.gridOptionsKey, this.searchParams); // save current search terms to local storage to persist across sessions
    this.isLoadingResults = true; // Flip flag to show that results are loading.
    this.loadingSearchResults = true;
    this.getPageData();
  }


  hasCurrentAdvancedSearchParams(): boolean {
    if (this.searchParams['searchText'] && this.searchParams['searchText'] !== null) return true;
    if (this.searchParams['categories'] && this.searchParams['categories'] !== '') return true;
    if (this.searchParams['subCategories'] && this.searchParams['subCategories'] !== '') return true;
    return false;
  }


  private resolveGridSort(): { field: string; direction: string; shuffle: boolean } {
    switch (this.currentSort) {
      case 'alphabetical':
        return { field: 'title', direction: 'asc', shuffle: false };
      case 'newest':
        return { field: 'createdAt', direction: 'desc', shuffle: false };
      case 'default':
      default:
        if (!this.hasCurrentAdvancedSearchParams()) {
          return { field: 'random', direction: 'desc', shuffle: true };
        }
        return { field: 'title', direction: 'asc', shuffle: false };
    }
  }


  onHeaderSearchTextChange(value: string) {
    this.searchParams.searchText = value;
    this.recalcDerivedState();
  }


  onHeaderSearchClear() {
    this.searchParams.searchText = '';
    this.doSearch();
  }


  onHeaderCategoryChange(name: string) {
    // Single-select chip overrides panel multi-select.
    this.selectedCategoriesMulti = [];
    this.searchParams.categories = name ?? '';
    this.subCategoryToShow = null;
    this.showSubCategorySelect = false;
    this.searchParams.subCategories = '';
    if (name) this.checkForSubs(name);
    this._materialSearchTermsService.saveAdditionalOptionsInLocal(this.gridOptionsKey, {
      selectedCategoriesMulti: []
    });
    this.doSearch();
  }


  onHeaderSortChange(sort: MaterialSortKey) {
    this.currentSort = sort;
    this._materialSearchTermsService.saveAdditionalOptionsInLocal(this.gridOptionsKey, { sort });
    this.resetPageNumbers();
    this.isLoadingResults = true;
    this.loadingSearchResults = true;
    this.getPageData();
  }


  onHeaderFiltersClick() {
    this.showFiltersPanel = true;
  }


  onFiltersPanelApplied(result: FiltersPanelResult) {
    this.selectedCategoriesMulti = result.selectedCategories ?? [];
    // Panel multi-select supersedes single-chip selection.
    if (this.selectedCategoriesMulti.length === 0) {
      this.searchParams.categories = '';
    } else if (this.selectedCategoriesMulti.length === 1) {
      this.searchParams.categories = this.selectedCategoriesMulti[0];
    } else {
      // Speculative: comma-list sent through `categories`. Single-category
      // path remains identical to chip behavior; multi-category support
      // depends on backend handling.
      this.searchParams.categories = this.selectedCategoriesMulti.join(',');
    }
    this.searchParams.subCategories = result.selectedSubCategory ?? '';
    this._materialSearchTermsService.saveAdditionalOptionsInLocal(this.gridOptionsKey, {
      selectedCategoriesMulti: this.selectedCategoriesMulti
    });
    this.showFiltersPanel = false;
    this.doSearch();
  }


  onFiltersPanelCleared() {
    this.selectedCategoriesMulti = [];
    this.searchParams.categories = '';
    this.searchParams.subCategories = '';
    this.subCategoryToShow = null;
    this.showSubCategorySelect = false;
    this._materialSearchTermsService.saveAdditionalOptionsInLocal(this.gridOptionsKey, {
      selectedCategoriesMulti: []
    });
    this.showFiltersPanel = false;
    this.doSearch();
  }


  onFiltersPanelClosed() {
    this.showFiltersPanel = false;
  }


  private recalcDerivedState() {
    let badge = 0;
    if (this.selectedCategoriesMulti?.length > 1) badge += this.selectedCategoriesMulti.length;
    if (this.searchParams.subCategories) badge += 1;
    this.filtersBadgeCount = badge;
    this.hasActiveFilters = this.hasCurrentAdvancedSearchParams();
  }

  trackById(_i: number, item: any): any {
    return item?._id ?? _i;
  }

  private syncPaginationConfig() {
    this.paginationConfig.itemsPerPage = this.itemsPerPage;
    this.paginationConfig.currentPage = this.currentPage;
    this.paginationConfig.totalItems = this.totalItemsCount;
  }


  clearActiveFilter(facet: 'searchText' | 'category' | 'subCategory' | 'multi', categoryName?: string) {
    if (facet === 'searchText') {
      this.searchParams.searchText = '';
    } else if (facet === 'category') {
      this.selectedCategoriesMulti = [];
      this.searchParams.categories = '';
      this.searchParams.subCategories = '';
      this.subCategoryToShow = null;
      this.showSubCategorySelect = false;
      this._materialSearchTermsService.saveAdditionalOptionsInLocal(this.gridOptionsKey, {
        selectedCategoriesMulti: []
      });
    } else if (facet === 'subCategory') {
      this.searchParams.subCategories = '';
    } else if (facet === 'multi' && categoryName) {
      this.selectedCategoriesMulti = this.selectedCategoriesMulti.filter(c => c !== categoryName);
      if (this.selectedCategoriesMulti.length === 0) {
        this.searchParams.categories = '';
        this.searchParams.subCategories = '';
      } else if (this.selectedCategoriesMulti.length === 1) {
        this.searchParams.categories = this.selectedCategoriesMulti[0];
      } else {
        this.searchParams.categories = this.selectedCategoriesMulti.join(',');
      }
      this._materialSearchTermsService.saveAdditionalOptionsInLocal(this.gridOptionsKey, {
        selectedCategoriesMulti: this.selectedCategoriesMulti
      });
    }
    this.doSearch();
  }


  getPageData() {
    let shuffleArray: boolean = false; // Whether we want to shuffle the results.

    merge()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        startWith({}),
        switchMap(() => {
          this.searchParams.deleted = false;
          if (this.currentViewMode === '4 Columns') {
            const pageToPass = this.currentPage - 1;
            const sortChoice = this.resolveGridSort();
            shuffleArray = sortChoice.shuffle;

            return this._materialService.getCustomDatasource(
              this.searchParams, sortChoice.field, sortChoice.direction, pageToPass, this.itemsPerPage
            );
          } else {
            if (this.sort?.active == undefined) shuffleArray = true;

            return this._materialService.getCustomDatasource(
              this.searchParams, this.sort?.active, this.sort?.direction, this.paginator?.pageIndex || 0, this.paginator?.pageSize || this.defaultPageSize
            );
          }
        }),
        map(data => {
          // this.isLoadingResults = false; // Flip flag to show that loading has finished.
          // this.loadingSearchResults = false;
          this.totalItemsCount = data['totalItemCount'];
          return data['message'];
        }),
        catchError(() => {
          this.isLoadingResults = false;
          this.loadingSearchResults = false;
          return of([]);
        })
      ).subscribe(_materials => {
        const retrievedMaterials = _materials?.length ? _materials : [];
        this.handleResults(retrievedMaterials, shuffleArray)
        // this.sortArray('Articulation Therapy', _materials);
      });
  }


  handleResults(arr: any[], _shouldShuffle: boolean = false) {
    if (_shouldShuffle) {
      const shuffledArr = this.customShuffle(arr);
      this.allMedia = shuffledArr;
      this.allMaterials = [...this.allMedia];
    } else {
      this.allMedia = arr;
      this.allMaterials = [...this.allMedia];
    }

    this.isLoadingResults = false; // Flip flag to show that loading has finished.
    this.loadingSearchResults = false;
    this.syncPaginationConfig();
  }


  customShuffle(arr) {
    // Shuffle twice to try to make it as random as possible.
    const shuffled = [...arr];

    // The Fisher-Yates shuffle algorithm
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // Shuffle by record title
    for (let i = 0; i < shuffled.length - 1; i++) {
      if (shuffled[i].title != undefined && shuffled[i + 1].title != undefined && shuffled[i].title === shuffled[i + 1].title) {
        const j = Math.min(i + 2, shuffled.length - 1);
        [shuffled[i + 1], shuffled[j]] = [shuffled[j], shuffled[i + 1]];
      }
    }

    return shuffled;
  }


  sortArray(value, arr) {
    arr.forEach(material => {
      material.categories.forEach(category => {
        if (category.name === value) {
          let index = arr.indexOf(material);
          arr.splice(index, 1);
          arr.unshift(material);
        }
      });
    });
    this.allMedia = arr;
    this.allMaterials = [...this.allMedia];
  }


  clearFilter() {
    this.subCategoryToShow = null;
    this.showSubCategorySelect = false;
    this.selectedCategoriesMulti = [];
    this.searchParams = new MediaFile();
    this._materialSearchTermsService.saveAdditionalOptionsInLocal(this.gridOptionsKey, {
      selectedCategoriesMulti: []
    });
    this.doSearch();
  }


  doSearch() {
    this.resetPageNumbers();
    this.applySearchResults();
  }


  async showAddMaterialDialog() {
    const dialogRef = this.addMaterialDialog.open(AddMaterialDialogComponent, { width: '80%', maxWidth: '700px', disableClose: true, panelClass: 'material-add-dialog' });

    const instance = dialogRef.componentInstance;
    instance.materialCategories = this.materialCategories ?? [];

    const _creationResults = await lastValueFrom(dialogRef.afterClosed());
    // console.log('_creationResults: ', _creationResults);

    if (_creationResults?.saveHappened && _creationResults?.materialTOPassBack?._id) {
      this.currentPage = 1;
      if (this.paginator) this.paginator.pageIndex = 0;

      this.isLoadingResults = true; // Flip flag to show that results are loading.
      this.loadingSearchResults = true;
      this.getPageData();
    }
  }


  async showMaterialDetail(item: any) {
    this.selectedItem = item;
    this._materialHelperService.setMaterialToBeViewed(this.selectedItem); // Pushing material item to service.

    if (this.currentUser?._id) {
      this.newMaterialAction = new MaterialAction();
      this.newMaterialAction.actionType = 'visit';
      this.newMaterialAction.visitor = this.currentUser._id;
      this.newMaterialAction.owner = this.selectedItem?.user?._id ?? this.selectedItem?.user ?? null;
      this.newMaterialAction.material = this.selectedItem?._id;

      try {
        const resMaterialAction = await lastValueFrom(this._materialActionService.save(this.newMaterialAction));

        if (resMaterialAction) {
          this.actionStore.push(resMaterialAction);
          this.newMaterialAction = resMaterialAction;
        } else {
          console.log('There was a problem creating the visit action.');
        }
      } catch (_err) {
        console.log('Error creating material action: ', _err);
      }
    }

    this._router.navigate(['/materials/detailed-view/' + this.selectedItem?._id]);
  }


  goToUsersProfile(item: any) {
    // if (this.currentUser?._id) {
    //   if (item.user._id === this.currentUser._id) this._router.navigate(['/material/user-profile']);
    //   else {
    //     if (item.user._id) this._router.navigate(['/material/user-profile/' + item.user._id]);
    //     else this._sharedService.showErrorDialog('This material has no current user by that name. User Not Found', 6000);
    //   }
    // }
  }
}